import { fork, all, put, takeEvery, select } from 'redux-saga/effects';

import { simpleRequestsLoader } from 'modules/app/utils';
import * as types from 'modules/contactLists/actionTypes';
import { UPLOAD_MEDIAS_SUCCESS } from 'modules/medias/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';

import dashboardSaga from './dashboard';
import detailsSaga from './details';
import {
  closeAddContactsModal,
  deleteContactListFailure,
  deleteContactListSuccess,
} from '../actions/core';
import { META_SOURCE_UPLOAD } from '../constants';
import { getSelectedContactListId } from '../selectors/details';

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.CREATE_CONTACT_LIST]: {
    notifications: {
      success: {
        id: 'page.contactLists.create.notification.success',
        defaultMessage: 'The contact list has been created with success.',
      },
      failure: {
        id: 'page.contactLists.create.notification.failure',
        defaultMessage: 'An error occurred during the contact list creation.',
      },
    },
  },
  [types.ADD_CONTACTS]: {
    notifications: {
      success: {
        id: 'page.contactLists.details.contactMember.add.notification.success',
        defaultMessage: 'The contacts have been added with success.',
      },
      failure: ({ payload }) => {
        const { errorType } = payload.response?.data ?? {};
        if (errorType) {
          return {
            id: `page.contactLists.details.contactMember.add.notification.failure.type.${errorType}`,
            defaultMessage: 'The contact already exists in the list.',
          };
        }
        return {
          id: 'page.contactLists.details.contactMember.add.notification.failure.generic',
          defaultMessage: 'An error occurred while adding the contact.',
        };
      },
    },
  },
  [types.UPDATE_CONTACT_LIST]: {
    notifications: {
      success: {
        id: 'page.contactLists.update.notification.success',
        defaultMessage: 'The contact list has been updated with success.',
      },
      failure: {
        id: 'page.contactLists.update.notification.failure',
        defaultMessage: 'An error occurred during the contact list update.',
      },
    },
  },
  [types.DELETE_CONTACT_LIST]: {
    callbacks: {
      success: (_, meta, { payload }) => deleteContactListSuccess(payload.contactListId, meta),
      failure: (error, meta, { payload }) => deleteContactListFailure(payload.contactListId, error, meta),
    },
    notifications: {
      success: {
        id: 'page.contactLists.delete.success',
        defaultMessage: 'The contact list has been deleted with success.',
      },
      failure: {
        id: 'page.contactLists.delete.failure',
        defaultMessage: 'An error occurred during the contact list deletion.',
      },
    },
  },
  [types.EXPORT_CONTACT_LIST]: {
    notifications: {
      success: {
        id: 'page.contactLists.details.export.notification.success',
        defaultMessage: 'The contact list has been exported with success.',
      },
      failure: {
        id: 'page.contactLists.details.export.notification.failure',
        defaultMessage: 'An error occurred during the contact list export.',
      },
    },
  },
};

function* afterUploadContactListFile() {
  const entityId = yield select(getSelectedContactListId);
  yield put(closeAddContactsModal(entityId, { source: META_SOURCE_UPLOAD }));
  yield put(
    createNotification({
      type: 'info',
      message: {
        id: 'page.contactLists.details.contactMember.add.template.notification.info',
        defaultMessage: 'The document is being processed, you will be notified by email when the processing is done.',
      },
    })
  );
}

export default function* contactListsSaga() {
  yield all(simpleRequestsLoader(simpleRequests));
  yield fork(dashboardSaga);
  yield fork(detailsSaga);
  yield takeEvery(
    ({ type, meta }) => type === UPLOAD_MEDIAS_SUCCESS && meta.source === META_SOURCE_UPLOAD,
    afterUploadContactListFile,
  );
}
